import type {MutationProposal} from "./mutationProposal";
import {readMutationExecution} from "./mutationExecution";
import {isFailedToolResult, parseToolResult} from "./codeExecution";

export type ToolApprovalStatus = "submitting" | "denied" | "approved" | "completed" | "reviewed" | "indeterminate" | "unavailable" | "required" | "failed" | "stale" | "expired" | "permissionLost" | "targetMissing";

/**
 * Settled outcomes that need more than their label, most urgent first. A stale or expired
 * proposal changed nothing, so the owner may ask Gail for a fresh one.
 */
const OUTCOME_NOTICES = [
   {status: "stale", notice: "staleDetail", tone: "warning", requestFresh: true},
   {status: "expired", notice: "expiredDetail", tone: "warning", requestFresh: true},
   {status: "permissionLost", notice: "permissionLostDetail", tone: "error", requestFresh: false},
   {status: "targetMissing", notice: "targetMissingDetail", tone: "error", requestFresh: false},
   {status: "failed", notice: "failedDetail", tone: "error", requestFresh: false},
   {status: "indeterminate", notice: "indeterminateDetail", tone: "warning", requestFresh: false},
] as const satisfies readonly {status: ToolApprovalStatus; notice: string; tone: "warning" | "error"; requestFresh: boolean}[];

export type OutcomeNotice = (typeof OUTCOME_NOTICES)[number];

export const outcomeNotice = (status: ToolApprovalStatus): OutcomeNotice | undefined =>
   OUTCOME_NOTICES.find(outcome => outcome.status === status);

/**
 * Wingman reports an approved Gail mutation that did not execute as
 * `{status: "rejected" | "indeterminate", unknown, reason}` rather than as a tool error.
 * A rejection (including a stale preview) changed nothing; an indeterminate one may have.
 */
function executionStatus(result: Record<string, unknown> | null): ToolApprovalStatus {
   if (result?.status === "rejected") return "failed";
   if (result?.status === "indeterminate" || result?.unknown === true) return "indeterminate";
   return isFailedToolResult(result) ? "failed" : "completed";
}

/**
 * Wingman's denial reason for an approval that expired before execution. It carries no
 * structured code, so this must match `gailApprovalResultPart` in Wingman exactly.
 */
const EXPIRED_APPROVAL_REASON = "Approval expired; no change was made.";
const STALE_PREVIEW_CODES = new Set(["ERR_USE_CASE_PREVIEW_STALE", "ERR_SCRIPT_PREVIEW_STALE"]);

/** Only inspect transport envelopes, never saved content that may contain these same keys. */
function envelopeFailureStatus(output: Record<string, unknown> | null): ToolApprovalStatus | undefined {
   const queue = [output];
   const seen = new Set<Record<string, unknown>>();
   let httpStatus: ToolApprovalStatus | undefined;
   for (const envelope of queue) {
      if (!envelope || seen.has(envelope)) continue;
      seen.add(envelope);
      if ([envelope.code, envelope.errorCode].some(code => typeof code === "string" && STALE_PREVIEW_CODES.has(code))) return "stale";
      if (envelope.statusCode === 403) httpStatus ??= "permissionLost";
      if (envelope.statusCode === 404) httpStatus ??= "targetMissing";
      queue.push(parseToolResult(envelope.error), parseToolResult(envelope.response));
   }
   return httpStatus;
}

export function resolveToolApprovalStatus({approval, completed, active, result, pending = false}: {
   approval?: {id: string; approved?: boolean; reason?: string; proposal?: MutationProposal};
   completed: boolean;
   active: boolean;
   result?: unknown;
   /** A decision from this client is in flight. */
   pending?: boolean;
}): ToolApprovalStatus {
   if (approval?.approved === false) return approval.reason === EXPIRED_APPROVAL_REASON ? "expired" : "denied";
   if (completed) {
      const output = parseToolResult(result);
      if (output?.unknown === true || output?.status === "indeterminate") return "indeterminate";
      const failure = envelopeFailureStatus(output);
      if (failure) return failure;
      const status = executionStatus(output);
      if (approval?.proposal && status === "completed" && !readMutationExecution(result, approval.proposal)) return "indeterminate";
      return status;
   }
   if (pending) return "submitting";
   if (approval?.approved === true) return "approved";
   if (!active) return "unavailable";
   return "required";
}

/** One label for a batch of proposals. A mix of applied and canceled proposals is reviewed. */
export function combinedApprovalStatus(statuses: ToolApprovalStatus[], pending: boolean): ToolApprovalStatus {
   const outcome = OUTCOME_NOTICES.find(({status}) => statuses.includes(status));
   if (outcome) return outcome.status;
   if (statuses.every(value => value === "completed")) return "completed";
   if (pending) return "submitting";
   if (statuses.includes("required")) return "required";
   if (statuses.includes("approved")) return "approved";
   if (statuses.every(value => value === "denied")) return "denied";
   if (statuses.every(value => value === "completed" || value === "denied")) return "reviewed";
   return "unavailable";
}
